import { MessageSquare, Users, BarChart3, Zap, Shield, Globe, Play, X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useState } from 'react';

const features = [
  {
    icon: MessageSquare,
    title: 'Bulk Messaging',
    description: 'Send thousands of personalized WhatsApp messages to your contacts in seconds',
    color: 'bg-blue-100 text-blue-600',
  },
  {
    icon: Users,
    title: 'Contact Management',
    description: 'Organize and segment your contacts for targeted campaigns',
    color: 'bg-purple-100 text-purple-600',
  },
  {
    icon: BarChart3,
    title: 'Analytics & Reports',
    description: 'Track message delivery, open rates, and engagement metrics in real-time',
    color: 'bg-green-100 text-green-600',
  },
  {
    icon: Zap,
    title: 'Automation',
    description: 'Set up automated responses and chatbots to handle customer inquiries',
    color: 'bg-yellow-100 text-yellow-600',
  },
  {
    icon: Shield,
    title: 'Meta Compliant',
    description: 'Enterprise-grade security with GDPR compliance and data encryption',
    color: 'bg-red-100 text-red-600',
  },
  {
    icon: Globe,
    title: 'Multi-Language',
    description: 'Support for 100+ languages to reach global audiences',
    color: 'bg-indigo-100 text-indigo-600',
  },
];

// YouTube Video ID - Replace with your actual video ID
const YOUTUBE_VIDEO_ID = 'dQw4w9WgXcQ'; // Replace with actual GiriChat demo video ID

export function Features() {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const openVideo = () => {
    setIsVideoOpen(true);
    // Track video play event with GA
    if (window.gtag) {
      window.gtag('event', 'video_start', {
        video_title: 'GiriChat Demo Video',
        video_provider: 'youtube'
      });
    }
  };

  const closeVideo = () => {
    setIsVideoOpen(false);
  };

  return (
    <div className="bg-white py-12 sm:py-16 lg:py-20 px-4" id="features" data-testid="features-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 sm:mb-12 lg:mb-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Everything you need to succeed
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
            Powerful features to help you grow your business with WhatsApp Business API
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <Card key={index} className="p-5 sm:p-6 hover:shadow-lg transition-shadow border border-gray-200">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg ${feature.color} flex items-center justify-center mb-3 sm:mb-4`}>
                <feature.icon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
              <p className="text-sm sm:text-base text-gray-600">{feature.description}</p>
            </Card>
          ))}
        </div>

        {/* Video Demo Section */}
        <div className="mt-10 sm:mt-12 lg:mt-16 bg-gradient-to-br from-[#E8F9EE] to-[#D4F4E0] rounded-xl sm:rounded-2xl p-6 sm:p-8 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
                See it in action
              </h3>
              <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6">
                Watch how businesses like yours are using GiriChat to transform their customer communication and drive growth.
              </p>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#00D95F] flex items-center justify-center">
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="text-sm sm:text-base text-gray-700">Easy to set up and use</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#00D95F] flex items-center justify-center">
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="text-sm sm:text-base text-gray-700">No technical knowledge required</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#00D95F] flex items-center justify-center">
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="text-sm sm:text-base text-gray-700">Results in minutes, not days</span>
                </div>
              </div>
            </div>
            
            {/* Video Player Section */}
            <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-gray-200">
              <div 
                className="aspect-video bg-gray-900 relative cursor-pointer group"
                onClick={openVideo}
                data-testid="video-thumbnail"
              >
                {/* YouTube Thumbnail */}
                <img 
                  src={`https://img.youtube.com/vi/${YOUTUBE_VIDEO_ID}/maxresdefault.jpg`}
                  alt="GiriChat Demo Video"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to medium quality thumbnail if maxres doesn't exist
                    e.target.src = `https://img.youtube.com/vi/${YOUTUBE_VIDEO_ID}/hqdefault.jpg`;
                  }}
                />
                
                {/* Play Button Overlay */}
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#00D95F] rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Play className="w-8 h-8 sm:w-10 sm:h-10 text-white ml-1" fill="white" />
                  </div>
                </div>
                
                {/* YouTube Badge */}
                <div className="absolute bottom-4 right-4 bg-red-600 text-white px-3 py-1 rounded text-xs font-medium flex items-center gap-1">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  YouTube
                </div>
              </div>
              <div className="p-4 text-center">
                <p className="text-sm text-gray-600">Click to watch the demo video</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {isVideoOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={closeVideo}
          data-testid="video-modal"
        >
          <div 
            className="relative w-full max-w-4xl aspect-video bg-black rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeVideo}
              className="absolute -top-10 right-0 sm:top-4 sm:right-4 z-10 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
              data-testid="video-close-btn"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* YouTube Embed */}
            <iframe
              src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`}
              title="GiriChat Demo Video"
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
}
